import React, { useCallback, useEffect, useMemo } from 'react';
import { DBReminderThunks, reminderSelectByQuery } from '@common/reducers/DBServiceThunks';
import { CommonRootState } from '@common/types/common-root-state-type';
import LazyLoadingVirtuoso from '@extension/context/content/components/bt-bulk-dashboard/lazy-loading-virtuoso';
import ReminderItem from './ReminderItem';
import { useAppDispatch } from '@common/reducers/hooks/store.hook';

type ReminderScope = 'current' | 'all';

interface RemindersListProps {
    scope?: ReminderScope;
    currentChatId?: string;
    height?: number | string;
}

const RemindersList: React.FC<RemindersListProps> = ({
    scope = 'all',
    currentChatId,
    height,
}) => {
    const dispatch = useAppDispatch();
    const listHeight = height ?? (typeof window !== 'undefined' ? window.innerHeight - 115 : 600);

    // DB Reducer selector
    const dbReducerSelector = (state: CommonRootState) => state.DBRemindersReducer;

    // Custom selector that sorts reminders: overdue first, then by date DESC
    const customSelectByQuery = useCallback((
        reducerState: any,
        filterObj: any,
        projectionArray: string[],
        sortObj: any
    ) => {
        // Get the base results (array of Reminder objects or IDs)
        const results = reminderSelectByQuery(reducerState, filterObj, projectionArray, sortObj);

        if (!results || !Array.isArray(results)) return results;

        const scopeFiltered = results.filter((item: any) => {
            if (scope !== 'current') return true;
            if (!currentChatId) return false;

            const reminder = typeof item === 'string' ? reducerState.entities?.[item] : item;
            return reminder?.relatedChatId === currentChatId;
        });

        const now = new Date();

        // Sort: overdue (not completed, dueAt passed) first, then by dueAt DESC
        const sorted = [...scopeFiltered].sort((a: any, b: any) => {
            // Handle both ID strings and Reminder objects
            const reminderA = typeof a === 'string' ? reducerState.entities?.[a] : a;
            const reminderB = typeof b === 'string' ? reducerState.entities?.[b] : b;

            if (!reminderA || !reminderB) return 0;

            const dueAtA = reminderA.dueAt ? new Date(reminderA.dueAt) : null;
            const dueAtB = reminderB.dueAt ? new Date(reminderB.dueAt) : null;

            // isActive: false means completed, isActive: undefined/true means active
            const isOverdueA = dueAtA && dueAtA < now && reminderA.isActive !== false;
            const isOverdueB = dueAtB && dueAtB < now && reminderB.isActive !== false;

            // Overdue items come first
            if (isOverdueA && !isOverdueB) return -1;
            if (!isOverdueA && isOverdueB) return 1;

            // Within same group, sort by dueAt DESC (newest first)
            if (dueAtA && dueAtB) {
                return dueAtB.getTime() - dueAtA.getTime();
            }

            return 0;
        });

        return sorted;
    }, [scope, currentChatId]);

    const remindersSelector = useCallback((state: CommonRootState) => {
        return customSelectByQuery(state.DBRemindersReducer, {}, [], {});
    }, [customSelectByQuery]);

    // Create a wrapper component that passes the props to ReminderItem
    const ListItem = useCallback(({ itemId, index }: { itemId: string; index?: number }) => (
        <ReminderItem
            itemId={itemId}
            index={index}
        />
    ), []);

    useEffect(() => {
        // Fetch reminders on mount
        dispatch(DBReminderThunks.find({}));
    }, [dispatch]);

    // Empty object for filters - memoized to prevent re-renders
    const filterObj = useMemo(() => ({}), []);

    return (
        <div className="h-full w-full">
            <LazyLoadingVirtuoso
                height={listHeight}
                filterObj={filterObj}
                DBThunks={DBReminderThunks}
                DBReducer={dbReducerSelector}
                selectByQuery={customSelectByQuery}
                selector={remindersSelector}
                Component={ListItem}
                featchData={false}
                rowHeightString="auto"
            />
        </div>
    );
};

export default RemindersList;
